/**
 * Suggesting a password for somebody else's sign-in.
 *
 * The same shape and the same alphabet as the server's generator in
 * api/src/domain/password.ts, and deliberately a separate copy rather than a
 * shared module: this one runs in a browser with crypto.getRandomValues,
 * that one runs in a function with node:crypto, and the only thing they
 * genuinely share is a decision about which characters a person can read
 * aloud without being asked to repeat themselves.
 *
 * Whatever this produces is validated by the server anyway. If the two ever
 * drift, the server wins and says why.
 */

/**
 * Letters and digits that cannot be confused with each other when read out
 * or copied off a screen. No O or 0, no I, l or 1, no S or 5.
 */
const UPPER = 'ABCDEFGHJKMNPQRTUVWXY';
const LOWER = 'abcdefghjkmnpqrtuvwxy';
const DIGIT = '2346789';

/**
 * One character, uniformly.
 *
 * Rejection sampling rather than a modulo, which would quietly make the
 * first few characters of the alphabet likelier than the rest. The loop
 * discards at most a fraction of draws and is not worth avoiding.
 */
function pick(set: string): string {
  const limit = Math.floor(256 / set.length) * set.length;
  const byte = new Uint8Array(1);
  for (;;) {
    crypto.getRandomValues(byte);
    if (byte[0]! < limit) return set[byte[0]! % set.length]!;
  }
}

/**
 * A password an accountant can read down a telephone.
 *
 * Three groups of four, hyphenated: `Tkmr-9vhq-Xbdq`. Upper, lower, digit
 * and the hyphen are all present by construction, so the directory's
 * three-of-four rule is met every time.
 */
export function suggestPassword(): string {
  const every = UPPER + LOWER + DIGIT;
  const chars = [pick(UPPER), pick(LOWER), pick(DIGIT)];
  while (chars.length < 12) chars.push(pick(every));

  // Shuffled so the guaranteed three are not always in the first three
  // places, which would make the shape of every password we issue public.
  const order = new Uint32Array(chars.length);
  crypto.getRandomValues(order);
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = order[i]! % (i + 1);
    [chars[i], chars[j]] = [chars[j]!, chars[i]!];
  }

  return [chars.slice(0, 4), chars.slice(4, 8), chars.slice(8, 12)]
    .map((group) => group.join(''))
    .join('-');
}

/** Long enough and varied enough that the server will not send it back. */
export function looksStrongEnough(password: string): boolean {
  if (password.length < 12 || password !== password.trim()) return false;
  let classes = 0;
  if (/[a-z]/.test(password)) classes += 1;
  if (/[A-Z]/.test(password)) classes += 1;
  if (/[0-9]/.test(password)) classes += 1;
  if (/[^a-zA-Z0-9]/.test(password)) classes += 1;
  return classes >= 3;
}
